import React, { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  EditButton,
  ShowButton,
  Show,
  TabbedShowLayout,
  Tab,
  ReferenceManyField,
  ReferenceField,
  DateField,
  NumberField,
  Edit,
  TabbedForm,
  FormTab,
  TextInput,
  Create,
  ImageInput,
  ImageField,
  ArrayField,
  SingleFieldList,
  Button,
  useNotify,
  useRefresh,
  useRecordContext,
  BooleanInput,
  SelectArrayInput,
  FunctionField,
  DeleteButton,
  BooleanField,
  useCreatePath,
  useGetList,
  useCreate,
  useUpdate,
  useDelete,
  RecordContextProvider,
  ResourceContextProvider
} from 'react-admin';
// import { RichTextInput } from 'ra-input-rich-text'; // Commented out due to form context issues
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  Checkbox,
  Chip,
  FormControlLabel,
  TextField as MuiTextField,
  Divider,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, Edit as EditIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { CommentsManagement } from './comments';
import { useNavigate } from 'react-router-dom';
import { API_URL } from './apiConfig';
import { NightsField, AtPropertyOrArrivalField } from './bookings';

const DEFAULT_GUEST_COUNTS = [2, 3, 4];

const GUEST_COUNT_CHOICES = Array.from({ length: 8 }, (_, index) => {
  const value = index + 1;
  return {
    id: value,
    name: `${value} guest${value > 1 ? 's' : ''}`,
  };
});

const normaliseGuestCounts = (value) => (
  Array.isArray(value)
    ? [...new Set(
        value
          .map((item) => Number(item))
          .filter((item) => !Number.isNaN(item) && item > 0),
      )].sort((a, b) => a - b)
    : []
);

const parseGuestCounts = normaliseGuestCounts;
const formatGuestCounts = normaliseGuestCounts;

const resolveGuestCounts = (record) => {
  if (!record) {
    return DEFAULT_GUEST_COUNTS;
  }

  const counts = normaliseGuestCounts(record.supported_guest_counts);

  if (!counts.length) {
    return DEFAULT_GUEST_COUNTS;
  }

  return counts;
};

const formatPriceValue = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }
  return numericValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatPriceSummary = (prices) => {
  if (!prices || typeof prices !== 'object') {
    return '—';
  }

  return Object.entries(prices)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([count, value]) => `${count} guests: ${formatPriceValue(value)}`)
    .join(', ');
};

const guestCountSummary = (counts) => {
  if (!counts.length) {
    return 'No specific guest counts set (defaults applied)';
  }
  return counts.map((count) => `${count} guest${count > 1 ? 's' : ''}`).join(', ');
};

// Custom HTML Editor Component
const CustomHtmlEditor = ({ value, onChange, label, placeholder }) => {
  const [htmlContent, setHtmlContent] = useState(value || '');
  const [showPreview, setShowPreview] = useState(false);

  const handleChange = (newValue) => {
    setHtmlContent(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const insertFormatting = (format) => {
    const textarea = document.getElementById(`editor-${label}`);
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = htmlContent.substring(start, end);
    
    let newText = '';
    switch (format) {
      case 'bold':
        newText = htmlContent.substring(0, start) + `<strong>${selectedText || 'Bold text'}</strong>` + htmlContent.substring(end);
        break;
      case 'italic':
        newText = htmlContent.substring(0, start) + `<em>${selectedText || 'Italic text'}</em>` + htmlContent.substring(end);
        break;
      case 'underline':
        newText = htmlContent.substring(0, start) + `<u>${selectedText || 'Underlined text'}</u>` + htmlContent.substring(end);
        break;
      case 'br':
        newText = htmlContent.substring(0, start) + '<br>' + htmlContent.substring(end);
        break;
      case 'ul':
        newText = htmlContent.substring(0, start) + '<ul><li>List item</li></ul>' + htmlContent.substring(end);
        break;
      case 'ol':
        newText = htmlContent.substring(0, start) + '<ol><li>List item</li></ol>' + htmlContent.substring(end);
        break;
      default:
        return;
    }
    
    handleChange(newText);
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + newText.length - htmlContent.length, start + newText.length - htmlContent.length);
    }, 0);
  };

  return (
    <Box>
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        <Button 
          size="small" 
          variant="outlined" 
          onClick={() => insertFormatting('bold')}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <strong>B</strong>
        </Button>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={() => insertFormatting('italic')}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <em>I</em>
        </Button>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={() => insertFormatting('underline')}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <u>U</u>
        </Button>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={() => insertFormatting('br')}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          ↵
        </Button>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={() => insertFormatting('ul')}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          • List
        </Button>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={() => insertFormatting('ol')}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          1. List
        </Button>
        <Button 
          size="small" 
          variant={showPreview ? "contained" : "outlined"}
          onClick={() => setShowPreview(!showPreview)}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          👁️ Preview
        </Button>
      </Box>
      
      {showPreview ? (
        <Box 
          border={1} 
          borderColor="grey.300" 
          borderRadius={1} 
          p={2} 
          minHeight={200}
          bgcolor="grey.50"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      ) : (
        <MuiTextField
          id={`editor-${label}`}
          value={htmlContent}
          onChange={(e) => handleChange(e.target.value)}
          multiline
          fullWidth
          rows={6}
          placeholder={placeholder || "Enter HTML content..."}
          variant="outlined"
          sx={{ fontFamily: 'monospace' }}
        />
      )}
      
      <Typography variant="caption" color="text.secondary" mt={1} display="block">
        Use HTML tags for formatting. Common tags: &lt;strong&gt;bold&lt;/strong&gt;, &lt;em&gt;italic&lt;/em&gt;, &lt;br&gt; (line break), &lt;ul&gt;&lt;li&gt;list&lt;/li&gt;&lt;/ul&gt;
      </Typography>
    </Box>
  );
};

// Custom Droppable to avoid issues with React.StrictMode
const StrictModeDroppable = ({ children, ...props }) => {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
        const animation = requestAnimationFrame(() => setEnabled(true));
        return () => {
            cancelAnimationFrame(animation);
            setEnabled(false);
        };
    }, []);
    if (!enabled) {
        return null;
    }
    return <Droppable {...props}>{children}</Droppable>;
};

// Helper to convert uploaded files to base64 strings
const convertFileToBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Parse photos from ImageInput back to an array of strings
const parsePhotos = async files => {
  if (!files) return [];
  const arr = Array.isArray(files) ? files : [files];
  const existing = arr.filter(f => !f.rawFile).map(f => f.src);
  const uploaded = await Promise.all(
    arr.filter(f => f.rawFile).map(file => convertFileToBase64(file.rawFile))
  );
  return [...existing, ...uploaded];
};

// Format photos from API to objects understood by ImageInput
const formatPhotos = photos =>
  Array.isArray(photos) ? photos.map(url => ({ src: url })) : [];

// Photos Management Component
const PhotosManagement = () => {
  const record = useRecordContext();
  if (!record) return null;

  const [photos, setPhotos] = useState(record.photos || []);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const notify = useNotify();
  const refresh = useRefresh();

  const updatePhotosOnServer = async (newPhotos) => {
    const token = localStorage.getItem('access_token');
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // The environment variable includes /api, but our endpoint is also under /api.
    // We construct the full URL to the custom endpoint.
    const endpoint = `${API_URL}/apartments/${record.id}/photos/`;

    const response = await fetch(endpoint, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ photos: newPhotos }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update photos.');
    }
    return response.json();
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handlePhotoUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      const newBase64Photos = await Promise.all(
        selectedFiles.map(convertFileToBase64)
      );
      const updatedPhotos = [...photos, ...newBase64Photos];
      
      await updatePhotosOnServer(updatedPhotos);
      
      setPhotos(updatedPhotos);
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      notify('Photos uploaded successfully', { type: 'success' });
      refresh();
    } catch (error) {
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  const handlePhotoDelete = async (index) => {
    try {
      const updatedPhotos = photos.filter((_, i) => i !== index);
      
      await updatePhotosOnServer(updatedPhotos);
      
      setPhotos(updatedPhotos);
      notify('Photo deleted successfully', { type: 'success' });
      refresh();
    } catch (error) {
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const reorderedPhotos = Array.from(photos);
    const [removed] = reorderedPhotos.splice(result.source.index, 1);
    reorderedPhotos.splice(result.destination.index, 0, removed);

    setPhotos(reorderedPhotos);
    updatePhotosOnServer(reorderedPhotos).catch(error => {
        notify(`Error saving new order: ${error.message}`, { type: 'error' });
        // Optionally revert state on error
        setPhotos(photos);
    });
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Photos Management</Typography>
          <Button
            label="Add Photos"
            onClick={() => setUploadDialogOpen(true)}
            startIcon={<AddIcon />}
          />
        </Box>

        {photos.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No photos uploaded yet. Click "Add Photos" to upload images.
          </Typography>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <StrictModeDroppable droppableId="photos" direction="horizontal">
              {(provided) => (
                <Box 
                  {...provided.droppableProps} 
                  ref={provided.innerRef}
                  display="grid" 
                  gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))" 
                  gap={2}
                >
                  {photos.map((photo, index) => (
                    <Draggable key={photo} draggableId={photo} index={index}>
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          position="relative"
                        >
                          <img
                            src={photo}
                            alt={`Photo ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '150px',
                              objectFit: 'cover',
                              borderRadius: '8px'
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handlePhotoDelete(index)}
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)'
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        )}

        <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Upload Photos</DialogTitle>
          <DialogContent>
            <Box mt={2}>
              <Input
                type="file"
                inputProps={{ multiple: true, accept: "image/*" }}
                onChange={handleFileSelect}
                fullWidth
              />
              {selectedFiles.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">
                    Selected {selectedFiles.length} file(s):
                  </Typography>
                  {selectedFiles.map((file, index) => (
                    <Typography key={index} variant="body2">
                      {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button label="Cancel" onClick={() => setUploadDialogOpen(false)} />
            <Button 
              label="Upload" 
              onClick={handlePhotoUpload}
              disabled={selectedFiles.length === 0}
            />
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Custom component for managing apartment descriptions and features
const DescriptionManagement = () => {
  const record = useRecordContext();
  const [descriptions, setDescriptions] = useState({
    description_pl: record?.description_pl || '',
    description_en: record?.description_en || '', 
    description_de: record?.description_de || ''
  });
  const [websiteTitles, setWebsiteTitles] = useState({
    website_title_pl: record?.website_title_pl || '',
    website_title_en: record?.website_title_en || '', 
    website_title_de: record?.website_title_de || ''
  });
  const [locationDescriptions, setLocationDescriptions] = useState({
    location_description_pl: record?.location_description_pl || '',
    location_description_en: record?.location_description_en || '', 
    location_description_de: record?.location_description_de || ''
  });
  const [locationDetails, setLocationDetails] = useState({
    slug: record?.slug || '',
    address: record?.address || '',
    zip_code: record?.zip_code || '',
    city: record?.city || '',
    latitude: record?.latitude || '',
    longitude: record?.longitude || '',
    email: record?.email || ''
  });
  const [domainConfig, setDomainConfig] = useState({
    custom_domain: record?.custom_domain || '',
    domain_enabled: record?.domain_enabled || false
  });
  const [features, setFeatures] = useState(record?.features || {});
  const [saving, setSaving] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  // Available features list
  const availableFeatures = [
    { key: 'entirePlace', label: 'Entire place for guests' },
    { key: 'wifi', label: 'Free Wi-Fi' },
    { key: 'parking', label: 'Free parking' },
    { key: 'kitchen', label: 'Kitchen' },
    { key: 'washingMachine', label: 'Washing machine' },
    { key: 'airConditioning', label: 'Air conditioning' },
    { key: 'heating', label: 'Heating' },
    { key: 'tv', label: 'TV' },
    { key: 'balcony', label: 'Balcony/Terrace' },
    { key: 'seaView', label: 'Sea view' },
    { key: 'mountainView', label: 'Mountain view' },
    { key: 'cityView', label: 'City view' },
    { key: 'garden', label: 'Garden' },
    { key: 'pool', label: 'Swimming pool' },
    { key: 'gym', label: 'Gym/Fitness' },
    { key: 'elevator', label: 'Elevator' },
    { key: 'petsAllowed', label: 'Pets allowed' },
    { key: 'smokingAllowed', label: 'Smoking allowed' },
    { key: 'familyFriendly', label: 'Family friendly' },
    { key: 'wheelchairAccessible', label: 'Wheelchair accessible' },
    { key: 'workspace', label: 'Dedicated workspace' },
    { key: 'coffeeMaker', label: 'Coffee maker' },
    { key: 'dishwasher', label: 'Dishwasher' },
    { key: 'oven', label: 'Oven' },
    { key: 'microwave', label: 'Microwave' },
    { key: 'refrigerator', label: 'Refrigerator' },
    { key: 'hairDryer', label: 'Hair dryer' },
    { key: 'iron', label: 'Iron' },
    { key: 'towels', label: 'Towels provided' },
    { key: 'bedLinens', label: 'Bed linens provided' },
    { key: 'fireplace', label: 'Fireplace' },
    { key: 'bbqGrill', label: 'BBQ grill' },
    { key: 'bicycles', label: 'Bicycles available' },
    { key: 'beachAccess', label: 'Beach access' },
    { key: 'skiInOut', label: 'Ski-in/Ski-out' }
  ];

  const handleDescriptionChange = (field, value) => {
    setDescriptions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWebsiteTitleChange = (field, value) => {
    setWebsiteTitles(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLocationDescriptionChange = (field, value) => {
    setLocationDescriptions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLocationDetailChange = (field, value) => {
    setLocationDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDomainConfigChange = (field, value) => {
    setDomainConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeatureToggle = (featureKey) => {
    setFeatures(prev => ({
      ...prev,
      [featureKey]: !prev[featureKey]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/apartments/${record.id}/`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          description_pl: descriptions.description_pl,
          description_en: descriptions.description_en,
          description_de: descriptions.description_de,
          website_title_pl: websiteTitles.website_title_pl,
          website_title_en: websiteTitles.website_title_en,
          website_title_de: websiteTitles.website_title_de,
          location_description_pl: locationDescriptions.location_description_pl,
          location_description_en: locationDescriptions.location_description_en,
          location_description_de: locationDescriptions.location_description_de,
          slug: locationDetails.slug,
          address: locationDetails.address,
          zip_code: locationDetails.zip_code,
          city: locationDetails.city,
          latitude: locationDetails.latitude,
          longitude: locationDetails.longitude,
          email: locationDetails.email,
          custom_domain: domainConfig.custom_domain,
          domain_enabled: domainConfig.domain_enabled,
          features: features
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update descriptions.');
      }

      notify('Descriptions and features updated successfully', { type: 'success' });
      refresh();
    } catch (error) {
      notify(`Error: ${error.message}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Apartment Details
        </Typography>
        
        {/* Location Details Section */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Location Details
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <MuiTextField
              label="URL Slug"
              value={locationDetails.slug}
              onChange={(e) => handleLocationDetailChange('slug', e.target.value)}
              fullWidth
              helperText="URL identifier (e.g. 'dziwnowek', 'zakopane')"
            />
            <MuiTextField
              label="Contact Email"
              type="email"
              value={locationDetails.email}
              onChange={(e) => handleLocationDetailChange('email', e.target.value)}
              fullWidth
            />
            <MuiTextField
              label="Street Address"
              value={locationDetails.address}
              onChange={(e) => handleLocationDetailChange('address', e.target.value)}
              fullWidth
            />
            <MuiTextField
              label="Postal Code"
              value={locationDetails.zip_code}
              onChange={(e) => handleLocationDetailChange('zip_code', e.target.value)}
              fullWidth
            />
            <MuiTextField
              label="City"
              value={locationDetails.city}
              onChange={(e) => handleLocationDetailChange('city', e.target.value)}
              fullWidth
            />
            <MuiTextField
              label="Latitude"
              type="number"
              value={locationDetails.latitude}
              onChange={(e) => handleLocationDetailChange('latitude', e.target.value)}
              fullWidth
              helperText="For Google Maps"
            />
            <MuiTextField
              label="Longitude"
              type="number"
              value={locationDetails.longitude}
              onChange={(e) => handleLocationDetailChange('longitude', e.target.value)}
              fullWidth
              helperText="For Google Maps"
            />
          </Box>
        </Box>

        {/* Domain Configuration Section */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Domain Configuration
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <MuiTextField
              label="Custom Domain"
              value={domainConfig.custom_domain}
              onChange={(e) => handleDomainConfigChange('custom_domain', e.target.value)}
              fullWidth
              helperText="Enter your custom domain (e.g. 'myapartment.com')"
              placeholder="myapartment.com"
            />
            <Box display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={domainConfig.domain_enabled}
                    onChange={(e) => handleDomainConfigChange('domain_enabled', e.target.checked)}
                    name="domain_enabled"
                  />
                }
                label="Enable Custom Domain"
              />
            </Box>
          </Box>
          {domainConfig.domain_enabled && domainConfig.custom_domain && (
            <Box mt={2} p={2} bgcolor="info.light" borderRadius={1}>
              <Typography variant="body2" color="info.contrastText">
                <strong>Domain Setup Instructions:</strong>
                <br />
                1. Point your domain's DNS to this server
                <br />
                2. Your apartment will be accessible at: <strong>https://{domainConfig.custom_domain}</strong>
                <br />
                3. Make sure SSL certificate is configured for your domain
              </Typography>
            </Box>
          )}
        </Box>

        {/* Website Titles Section */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Website Titles
          </Typography>
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              Polish Title
            </Typography>
            <MuiTextField
              value={websiteTitles.website_title_pl}
              onChange={(e) => handleWebsiteTitleChange('website_title_pl', e.target.value)}
              fullWidth
              placeholder="e.g. Apartament 340 - Widok na morze"
            />
          </Box>
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              English Title
            </Typography>
            <MuiTextField
              value={websiteTitles.website_title_en}
              onChange={(e) => handleWebsiteTitleChange('website_title_en', e.target.value)}
              fullWidth
              placeholder="e.g. Apartment 340 - Sea View"
            />
          </Box>
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              German Title
            </Typography>
            <MuiTextField
              value={websiteTitles.website_title_de}
              onChange={(e) => handleWebsiteTitleChange('website_title_de', e.target.value)}
              fullWidth
              placeholder="e.g. Apartment 340 - Meerblick"
            />
          </Box>
        </Box>

        {/* Descriptions Section */}
        <Typography variant="h6" gutterBottom>
          Apartment Descriptions
        </Typography>
        
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Polish Description
          </Typography>
          <CustomHtmlEditor
            value={descriptions.description_pl}
            onChange={(value) => handleDescriptionChange('description_pl', value)}
            label="polish"
            placeholder="Enter Polish description with HTML formatting..."
          />
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            English Description
          </Typography>
          <CustomHtmlEditor
            value={descriptions.description_en}
            onChange={(value) => handleDescriptionChange('description_en', value)}
            label="english"
            placeholder="Enter English description with HTML formatting..."
          />
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            German Description
          </Typography>
          <CustomHtmlEditor
            value={descriptions.description_de}
            onChange={(value) => handleDescriptionChange('description_de', value)}
            label="german"
            placeholder="Enter German description with HTML formatting..."
          />
        </Box>

        {/* Location Descriptions Section */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Location Descriptions
          </Typography>
          
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              Polish Location Description
            </Typography>
            <MuiTextField
              value={locationDescriptions.location_description_pl}
              onChange={(e) => handleLocationDescriptionChange('location_description_pl', e.target.value)}
              multiline
              fullWidth
              rows={3}
              helperText="Describe the location in Polish"
              variant="outlined"
            />
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              English Location Description
            </Typography>
            <MuiTextField
              value={locationDescriptions.location_description_en}
              onChange={(e) => handleLocationDescriptionChange('location_description_en', e.target.value)}
              multiline
              fullWidth
              rows={3}
              helperText="Describe the location in English"
              variant="outlined"
            />
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              German Location Description
            </Typography>
            <MuiTextField
              value={locationDescriptions.location_description_de}
              onChange={(e) => handleLocationDescriptionChange('location_description_de', e.target.value)}
              multiline
              fullWidth
              rows={3}
              helperText="Describe the location in German"
              variant="outlined"
            />
          </Box>
        </Box>

        <Typography variant="h6" gutterBottom>
          Features & Amenities
        </Typography>
        
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(250px, 1fr))" gap={1} mb={3}>
          {availableFeatures.map((feature) => (
            <FormControlLabel
              key={feature.key}
              control={
                <Checkbox
                  checked={features[feature.key] || false}
                  onChange={() => handleFeatureToggle(feature.key)}
                  name={feature.key}
                />
              }
              label={feature.label}
            />
          ))}
        </Box>

        <Box mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export const ApartmentList = props => (
  <List {...props}>
    <Datagrid rowClick="show">
      <TextField source="id" />
      <TextField source="name" />
      <TextField source="location" />
      <EditButton />
    </Datagrid>
  </List>
);

const SupportedGuestCountsView = () => {
  const record = useRecordContext();
  const counts = resolveGuestCounts(record);
  const isFallback = !record?.supported_guest_counts || !record.supported_guest_counts.length;

  return (
    <Box>
      <Typography variant="body2" color="text.primary">
        {guestCountSummary(counts)}
      </Typography>
      {isFallback && (
        <Typography variant="caption" color="text.secondary">
          Using default guest counts until customized.
        </Typography>
      )}
    </Box>
  );
};

const PricingGrid = ({ allowDelete = false }) => (
  <ReferenceManyField
    reference="apartment-prices"
    target="apartment"
    label=""
    perPage={25}
    pagination={false}
    sort={{ field: 'is_base', order: 'DESC' }}
  >
    <Datagrid
      bulkActionButtons={false}
      rowClick={allowDelete ? 'edit' : false}
      size="small"
      empty={<Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No pricing defined yet.</Typography>}
    >
      <BooleanField source="is_base" label="Base" />
      <DateField source="start_date" label="Start" />
      <DateField source="end_date" label="End" />
      <FunctionField label="Prices" render={(record) => formatPriceSummary(record.prices)} />
      <TextField source="notes" label="Notes" />
      <EditButton />
      {allowDelete && <DeleteButton mutationMode="pessimistic" />}
    </Datagrid>
  </ReferenceManyField>
);

// ---- YEARLY PRICING VIEW ----

// Pure date helpers (no library needed)
const addDays = (dateStr, n) => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const dayBefore = (dateStr) => addDays(dateStr, -1);
const dayAfter = (dateStr) => addDays(dateStr, 1);
const dayCount = (startStr, endStr) => {
  const start = new Date(startStr + 'T12:00:00');
  const end = new Date(endStr + 'T12:00:00');
  return Math.round((end - start) / 86400000) + 1;
};
const formatDatePl = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const pad = (n) => String(n).padStart(2, '0');

const MONTH_NAMES_PL = [
  'Styczeń','Luty','Marzec','Kwiecień','Maj','Czerwiec',
  'Lipiec','Sierpień','Wrzesień','Październik','Listopad','Grudzień',
];

const shortMonthPl = (m) => MONTH_NAMES_PL[m - 1]?.slice(0, 3).toLowerCase() ?? '';

const getMonthInfo = (year) => {
  const months = [];
  for (let m = 0; m < 12; m++) {
    const end = new Date(year, m + 1, 0);
    const days = end.getDate();
    const startStr = `${year}-${String(m + 1).padStart(2, '0')}-01`;
    const endStr = `${year}-${String(m + 1).padStart(2, '0')}-${String(days).padStart(2, '0')}`;
    const name = new Date(year, m, 1).toLocaleDateString('pl-PL', { month: 'long' });
    months.push({ index: m, name, days, startStr, endStr });
  }
  return months;
};

/** Build a map of dateStr → {color, name, priority} for every day in year, resolved by priority. */
const resolveEffectiveDayMap = (periodPrices, year) => {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const relevant = periodPrices
    .filter(p => p.end_date >= yearStart && p.start_date <= yearEnd)
    .sort((a, b) => (b.priority ?? 1) - (a.priority ?? 1)); // highest priority first

  const map = new Map();
  let d = new Date(yearStart + 'T12:00:00');
  const end = new Date(yearEnd + 'T12:00:00');
  while (d <= end) {
    const ds = d.toISOString().slice(0, 10);
    for (const p of relevant) {
      if (p.start_date <= ds && p.end_date >= ds) {
        map.set(ds, { color: p.color || '#1976d2', name: p.name || p.notes || '', priority: p.priority ?? 1, price: p });
        break;
      }
    }
    d.setDate(d.getDate() + 1);
  }
  return map;
};

/** For the layered system: returns all period prices sorted by priority desc, then start_date */
const buildYearPeriods = (periodPrices, year) => {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  return periodPrices
    .filter(p => p.end_date >= yearStart && p.start_date <= yearEnd)
    .sort((a, b) => {
      const sd = a.start_date.localeCompare(b.start_date);
      if (sd !== 0) return sd;
      return a.end_date.localeCompare(b.end_date);
    });
};

const YearSelector = ({ year, onPrev, onNext }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <IconButton size="small" onClick={onPrev}><ChevronLeftIcon /></IconButton>
    <Typography variant="h6" sx={{ minWidth: 60, textAlign: 'center' }}>{year}</Typography>
    <IconButton size="small" onClick={onNext}><ChevronRightIcon /></IconButton>
  </Box>
);

const EffectivePriceBar = ({ year, periodPrices }) => {
  const months = getMonthInfo(year);
  const yearDays = months.reduce((sum, m) => sum + m.days, 0);
  const dayMap = resolveEffectiveDayMap(periodPrices, year);

  return (
    <Box sx={{ display: 'flex', height: 32, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 2 }}>
      {months.map((month) => {
        // Build colored segments for this month by grouping consecutive days with same color
        const segments = [];
        let segStart = null;
        let segColor = null;
        let segName = null;
        let segDays = 0;

        for (let day = 1; day <= month.days; day++) {
          const ds = `${year}-${String(month.index + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const info = dayMap.get(ds);
          const color = info?.color ?? null;
          const name = info?.name ?? null;

          if (color === segColor) {
            segDays++;
          } else {
            if (segColor !== null && segStart !== null) {
              segments.push({ startDay: segStart, days: segDays, color: segColor, name: segName });
            }
            segStart = day - 1;
            segColor = color;
            segName = name;
            segDays = 1;
          }
        }
        if (segColor !== null && segStart !== null) {
          segments.push({ startDay: segStart, days: segDays, color: segColor, name: segName });
        }

        return (
          <Tooltip key={month.index} title={month.name} arrow>
            <Box sx={{
              flex: `${month.days / yearDays}`,
              position: 'relative',
              bgcolor: 'grey.200',
              borderRight: month.index < 11 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}>
              {segments.map((seg, i) => (
                <Tooltip key={i} title={seg.name ? `${month.name}: ${seg.name}` : month.name} arrow>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `${(seg.startDay / month.days) * 100}%`,
                      width: `${(seg.days / month.days) * 100}%`,
                      bgcolor: seg.color,
                      opacity: 0.8,
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

const BasePriceCard = ({ basePrice }) => {
  if (!basePrice) return null;
  return (
    <Box sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box>
        <Typography variant="body2" fontWeight="bold">Cena bazowa (domyślna)</Typography>
        <Typography variant="caption" color="text.secondary">{formatPriceSummary(basePrice.prices)}</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">Obowiązuje gdy brak okresu cenowego</Typography>
    </Box>
  );
};

const SEASON_DIALOG_DEFAULTS = {
  name: '',
  color: '#1976d2',
  priority: 1,
  start_day: 1,
  start_month: 1,
  end_day: 31,
  end_month: 12,
  min_nights: '',
};

const SeasonDialog = ({ open, onClose, season, apartmentId, guestCounts }) => {
  const [create, { isPending: isCreating }] = useCreate();
  const [update, { isPending: isUpdating }] = useUpdate();
  const notify = useNotify();

  const isEdit = Boolean(season?.id);
  const initialPrices = () => {
    const p = {};
    guestCounts.forEach(c => { p[String(c)] = season?.default_prices?.[String(c)] ?? ''; });
    return p;
  };

  const [form, setForm] = useState(() => {
    if (season) {
      return {
        name: season.name ?? '',
        color: season.color ?? '#1976d2',
        priority: season.priority ?? 1,
        start_day: season.start_day ?? 1,
        start_month: season.start_month ?? 1,
        end_day: season.end_day ?? 31,
        end_month: season.end_month ?? 12,
        min_nights: season.min_nights ?? '',
      };
    }
    return { ...SEASON_DIALOG_DEFAULTS };
  });
  const [prices, setPrices] = useState(initialPrices);

  // Reset when dialog opens/closes or season changes
  React.useEffect(() => {
    if (open) {
      if (season) {
        setForm({
          name: season.name ?? '',
          color: season.color ?? '#1976d2',
          priority: season.priority ?? 1,
          start_day: season.start_day ?? 1,
          start_month: season.start_month ?? 1,
          end_day: season.end_day ?? 31,
          end_month: season.end_month ?? 12,
          min_nights: season.min_nights ?? '',
        });
        const p = {};
        guestCounts.forEach(c => { p[String(c)] = season.default_prices?.[String(c)] ?? ''; });
        setPrices(p);
      } else {
        setForm({ ...SEASON_DIALOG_DEFAULTS });
        const p = {};
        guestCounts.forEach(c => { p[String(c)] = ''; });
        setPrices(p);
      }
    }
  }, [open, season, guestCounts]);

  const handleSave = () => {
    const defaultPrices = {};
    guestCounts.forEach(c => {
      const v = Number(prices[String(c)]);
      if (!Number.isNaN(v) && prices[String(c)] !== '') {
        defaultPrices[String(c)] = v;
      }
    });

    const data = {
      apartment: apartmentId,
      name: form.name.trim(),
      color: form.color,
      priority: Number(form.priority) || 1,
      start_month: Number(form.start_month),
      start_day: Number(form.start_day),
      end_month: Number(form.end_month),
      end_day: Number(form.end_day),
      default_prices: defaultPrices,
      min_nights: form.min_nights !== '' ? Number(form.min_nights) : null,
    };

    if (!data.name) {
      notify('Podaj nazwę sezonu', { type: 'error' });
      return;
    }

    if (isEdit) {
      update(
        'season-templates',
        { id: season.id, data, previousData: season },
        { onSuccess: () => onClose(), onError: (err) => notify(`Błąd: ${err.message}`, { type: 'error' }) }
      );
    } else {
      create(
        'season-templates',
        { data },
        { onSuccess: () => onClose(), onError: (err) => notify(`Błąd: ${err.message}`, { type: 'error' }) }
      );
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edytuj sezon' : 'Dodaj sezon'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Box display="flex" gap={2} alignItems="center">
          <MuiTextField
            label="Nazwa"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            size="small"
            sx={{ flex: 1 }}
            required
          />
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption">Kolor:</Typography>
            <input
              type="color"
              value={form.color}
              onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
              style={{ width: 36, height: 36, padding: 2, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
            />
          </Box>
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          <Typography variant="body2" sx={{ minWidth: 48 }}>Start:</Typography>
          <MuiTextField
            label="Dzień"
            type="number"
            value={form.start_day}
            onChange={e => setForm(f => ({ ...f, start_day: e.target.value }))}
            size="small"
            inputProps={{ min: 1, max: 31 }}
            sx={{ width: 80 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Miesiąc</InputLabel>
            <Select
              value={form.start_month}
              label="Miesiąc"
              onChange={e => setForm(f => ({ ...f, start_month: e.target.value }))}
            >
              {MONTH_NAMES_PL.map((name, i) => (
                <MenuItem key={i + 1} value={i + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          <Typography variant="body2" sx={{ minWidth: 48 }}>Koniec:</Typography>
          <MuiTextField
            label="Dzień"
            type="number"
            value={form.end_day}
            onChange={e => setForm(f => ({ ...f, end_day: e.target.value }))}
            size="small"
            inputProps={{ min: 1, max: 31 }}
            sx={{ width: 80 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Miesiąc</InputLabel>
            <Select
              value={form.end_month}
              label="Miesiąc"
              onChange={e => setForm(f => ({ ...f, end_month: e.target.value }))}
            >
              {MONTH_NAMES_PL.map((name, i) => (
                <MenuItem key={i + 1} value={i + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider />

        <Typography variant="subtitle2">Domyślne ceny (za dobę):</Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          {guestCounts.map(c => (
            <MuiTextField
              key={c}
              label={`${c} os.`}
              type="number"
              value={prices[String(c)] ?? ''}
              onChange={e => setPrices(p => ({ ...p, [String(c)]: e.target.value }))}
              size="small"
              inputProps={{ min: 0, step: 1 }}
              sx={{ width: 100 }}
            />
          ))}
        </Box>

        <Box display="flex" gap={2} alignItems="center">
          <MuiTextField
            label="Min. nocy (opcjonalnie)"
            type="number"
            value={form.min_nights}
            onChange={e => setForm(f => ({ ...f, min_nights: e.target.value }))}
            size="small"
            inputProps={{ min: 1, step: 1 }}
            sx={{ width: 180 }}
          />
          <MuiTextField
            label="Priorytet (1-10)"
            type="number"
            value={form.priority}
            onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
            size="small"
            inputProps={{ min: 1, max: 10, step: 1 }}
            sx={{ width: 140 }}
            helperText="1=sezonowe, 5=ferie, 10=premium"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Anuluj</Button>
        <Button onClick={handleSave} disabled={isPending} variant="contained">
          {isPending ? <CircularProgress size={18} /> : (isEdit ? 'Zapisz' : 'Dodaj')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SeasonScheduleManager = ({ seasons = [], apartmentId, guestCounts = [] }) => {
  const [deleteOne] = useDelete();
  const notify = useNotify();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);

  const handleOpenAdd = () => { setEditingSeason(null); setDialogOpen(true); };
  const handleOpenEdit = (season) => { setEditingSeason(season); setDialogOpen(true); };
  const handleClose = () => setDialogOpen(false);

  const handleDelete = (season) => {
    if (!window.confirm(`Usunąć sezon "${season.name}"?`)) return;
    deleteOne(
      'season-templates',
      { id: season.id, previousData: season },
      { onError: (err) => notify(`Błąd: ${err.message}`, { type: 'error' }) }
    );
  };

  const formatPeriod = (s) =>
    `${pad(s.start_day)}.${pad(s.start_month)} – ${pad(s.end_day)}.${pad(s.end_month)}`;

  const formatDefaultPrices = (dp) => {
    if (!dp || !Object.keys(dp).length) return '—';
    return Object.entries(dp)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([k, v]) => `${k} os: ${v}`)
      .join(', ');
  };

  return (
    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2">Szablony sezonów</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={handleOpenAdd}>
          Dodaj sezon
        </Button>
      </Box>

      {seasons.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Brak szablonów. Dodaj sezony, żeby móc zastosować roczny plan cenowy jednym kliknięciem.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Nazwa</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Okres</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Ceny</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Min</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>P</TableCell>
              <TableCell sx={{ py: 0.5 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {seasons.map(s => (
              <TableRow key={s.id} hover>
                <TableCell sx={{ py: 0.5 }}>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                    <Typography variant="body2">{s.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="body2">{formatPeriod(s)}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption">{formatDefaultPrices(s.default_prices)}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption">{s.min_nights ?? '—'}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption" fontWeight="bold">{s.priority ?? 1}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }} align="right">
                  <IconButton size="small" onClick={() => handleOpenEdit(s)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(s)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <SeasonDialog
        open={dialogOpen}
        onClose={handleClose}
        season={editingSeason}
        apartmentId={apartmentId}
        guestCounts={guestCounts}
      />
    </Box>
  );
};

const GapRow = ({ start, end, onAdd, seasons = [] }) => {
  const days = dayCount(start, end);
  return (
    <Box sx={{ border: '2px dashed', borderColor: 'warning.main', borderRadius: 1, p: 1.5, mb: 1, bgcolor: 'warning.light' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={seasons.length ? 0.5 : 0}>
        <Box>
          <Typography variant="body2" color="warning.dark" fontWeight="bold">
            Brak ceny — {formatDatePl(start)} – {formatDatePl(end)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {days} dni bez ustawionej ceny (obowiązuje cena bazowa)
          </Typography>
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={() => onAdd(start, end, '')}>
          Bez nazwy
        </Button>
      </Box>
      {seasons.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
          {seasons.map(season => (
            <Chip
              key={season.id}
              label={season.name}
              size="small"
              onClick={() => onAdd(start, end, season.name)}
              sx={{ bgcolor: season.color, color: 'white', cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const PeriodRow = ({ price, allowDelete }) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'background.paper' }}>
    <Box display="flex" alignItems="flex-start" gap={1.5} flex={1}>
      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: price.color || '#1976d2', flexShrink: 0, mt: 0.4 }} />
      <Box flex={1}>
        <Box display="flex" alignItems="center" gap={1} mb={0.25}>
          <Chip label={`P${price.priority ?? 1}`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold', bgcolor: 'action.selected' }} />
          {price.name && (
            <Chip label={price.name} size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
          )}
          <Typography variant="body2" fontWeight="bold">
            {formatDatePl(price.start_date)} – {formatDatePl(price.end_date)}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {dayCount(price.start_date, price.end_date)} dni · {formatPriceSummary(price.prices)}
          {price.min_nights ? ` · min ${price.min_nights} noce` : ''}
        </Typography>
        {price.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {price.notes}
          </Typography>
        )}
      </Box>
    </Box>
    <Box display="flex" gap={1}>
      <ResourceContextProvider value="apartment-prices">
        <RecordContextProvider value={price}>
          <EditButton size="small" />
          {allowDelete && <DeleteButton size="small" mutationMode="pessimistic" />}
        </RecordContextProvider>
      </ResourceContextProvider>
    </Box>
  </Box>
);

const YearlyPricingView = ({ allowDelete = false }) => {
  const record = useRecordContext();
  const notify = useNotify();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [create] = useCreate();

  const { data: allPrices = [] } = useGetList('apartment-prices', {
    filter: { apartment: record?.id },
    sort: { field: 'start_date', order: 'ASC' },
    pagination: { page: 1, perPage: 100 },
  });

  const { data: seasons = [] } = useGetList('season-templates', {
    filter: { apartment: record?.id },
    sort: { field: 'start_month', order: 'ASC' },
    pagination: { page: 1, perPage: 100 },
    enabled: Boolean(record?.id),
  });

  if (!record) return null;

  const basePrice = allPrices.find(p => p.is_base) ?? null;
  const periodPrices = allPrices.filter(p => !p.is_base);
  const yearPeriods = buildYearPeriods(periodPrices, selectedYear);

  const guestCounts = resolveGuestCounts(record);

  const handleApplyTemplate = (year) => {
    const applied = [];
    const skipped = [];

    for (const season of seasons) {
      // Require at least one price defined in the template
      const hasAnyPrice = guestCounts.some(c => {
        const v = season.default_prices?.[String(c)];
        return v != null && v !== '';
      });
      if (!hasAnyPrice) { skipped.push(`${season.name} (brak cen)`); continue; }

      // Build prices for all required guest counts.
      // Priority: 1) explicit price in template, 2) pricing_rules on apartment, 3) nearest lower count fallback.
      const sortedCounts = [...guestCounts].sort((a, b) => a - b);
      const baseCount = sortedCounts[0];
      const prices = {};

      // Fill explicit prices from template
      for (const c of sortedCounts) {
        const v = season.default_prices?.[String(c)];
        if (v != null && v !== '') prices[String(c)] = Number(v);
      }

      const pricingRules = record.pricing_rules ?? {};
      const basePrice = prices[String(baseCount)];

      // Fill missing counts using pricing_rules, then fallback to nearest lower
      for (const c of sortedCounts) {
        if (prices[String(c)] != null) continue;
        const rule = pricingRules[String(c)];
        if (rule && basePrice != null) {
          if (rule.type === 'percent') {
            prices[String(c)] = Math.round(basePrice * (1 + rule.value / 100));
          } else {
            prices[String(c)] = Math.round(basePrice + rule.value);
          }
        } else {
          // Fallback: nearest lower defined count
          const lower = [...sortedCounts].reverse().find(x => x < c && prices[String(x)] != null);
          const firstAvailable = Object.values(prices)[0];
          prices[String(c)] = lower != null ? prices[String(lower)] : firstAvailable;
        }
      }

      const startDate = `${year}-${pad(season.start_month)}-${pad(season.start_day)}`;
      const crossesYearEnd = season.end_month < season.start_month ||
        (season.end_month === season.start_month && season.end_day < season.start_day);
      const endYear = crossesYearEnd ? year + 1 : year;
      const endDate = `${endYear}-${pad(season.end_month)}-${pad(season.end_day)}`;

      // In layered system overlaps are allowed — no skip-on-overlap
      create('apartment-prices', {
        data: {
          apartment: record.id,
          is_base: false,
          start_date: startDate,
          end_date: endDate,
          name: season.name,
          prices,
          min_nights: season.min_nights || null,
          priority: season.priority || 1,
          color: season.color || '#1976d2',
          notes: '',
        }
      }, { onError: (err) => skipped.push(`${season.name}: ${err.message}`) });

      applied.push(season.name);
    }

    if (applied.length) notify(`Dodano ${applied.length} okres(ów)`, { type: 'success' });
    if (skipped.length) notify(`Pominięto: ${skipped.join(', ')}`, { type: 'warning' });
    if (!applied.length && !skipped.length) notify('Brak szablonów do zastosowania', { type: 'info' });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <YearSelector
          year={selectedYear}
          onPrev={() => setSelectedYear(y => y - 1)}
          onNext={() => setSelectedYear(y => y + 1)}
        />
        {seasons.length > 0 && (
          <Button size="small" onClick={() => handleApplyTemplate(selectedYear)}>
            Zastosuj szablon na {selectedYear}
          </Button>
        )}
      </Box>

      <EffectivePriceBar year={selectedYear} periodPrices={periodPrices} />

      <BasePriceCard basePrice={basePrice} />

      {yearPeriods.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Brak wpisów cenowych dla roku {selectedYear}.
        </Typography>
      ) : (
        yearPeriods.map((period) => (
          <PeriodRow key={`period-${period.id}`} price={period} allowDelete={allowDelete} />
        ))
      )}
    </Box>
  );
};

/** Edytor reguł cenowych — definiuje cenę dla N gości względem ceny bazowej (najmniejsza liczba gości). */
const PricingRulesEditor = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [isPending, setIsPending] = useState(false);

  const guestCounts = resolveGuestCounts(record);
  const baseCount = guestCounts.length ? Math.min(...guestCounts) : 2;
  const ruleTargets = guestCounts.filter(c => c !== baseCount);

  const buildInitialRules = (rec) => {
    const r = {};
    ruleTargets.forEach(c => {
      const existing = rec?.pricing_rules?.[String(c)];
      r[String(c)] = existing ? { type: existing.type, value: String(existing.value) } : { type: 'percent', value: '' };
    });
    // Special child rule
    const childExisting = rec?.pricing_rules?.child;
    r.child = childExisting ? { type: childExisting.type, value: String(childExisting.value) } : { type: 'fixed', value: '' };
    return r;
  };

  const [rules, setRules] = useState(() => buildInitialRules(record));

  useEffect(() => {
    setRules(buildInitialRules(record));
  }, [record?.id, ruleTargets.join(',')]) // eslint-disable-line;

  const handleSave = async () => {
    const cleanRules = {};
    Object.entries(rules).forEach(([k, v]) => {
      if (v.value !== '' && v.value != null) {
        cleanRules[k] = { type: v.type, value: Number(v.value) };
      }
    });
    setIsPending(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/apartments/${record.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ pricing_rules: cleanRules }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(JSON.stringify(err));
      }
      notify('Reguły zapisane', { type: 'success' });
    } catch (e) {
      notify(`Błąd: ${e.message}`, { type: 'error' });
    } finally {
      setIsPending(false);
    }
  };

  if (!record?.id) return null;

  if (ruleTargets.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Skonfiguruj więcej niż jedną obsługiwaną liczbę gości, aby dodać reguły cenowe.
      </Alert>
    );
  }

  const calcExample = (rule, base = 200) => {
    if (rule.value === '' || rule.value == null) return '—';
    const v = Number(rule.value);
    if (rule.type === 'percent') return `${base} + ${v}% = ${Math.round(base * (1 + v / 100))} zł`;
    return `${base} + ${v} zł = ${Math.round(base + v)} zł`;
  };

  return (
    <Box sx={{ mt: 2, mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        Reguły cenowe (względem ceny {baseCount}-osobowej)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Stosowane automatycznie przy zastosowaniu szablonu, gdy cena dla danej liczby gości nie jest jawnie ustawiona w szablonie.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Goście</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Typ modyfikatora</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Wartość</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Przykład (base=200 zł)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ruleTargets.map(c => {
            const rule = rules[String(c)] ?? { type: 'percent', value: '' };
            return (
              <TableRow key={c}>
                <TableCell>{c} os.</TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={rule.type}
                      onChange={e => setRules(r => ({ ...r, [String(c)]: { ...r[String(c)], type: e.target.value } }))}
                    >
                      <MenuItem value="percent">% (procent)</MenuItem>
                      <MenuItem value="fixed">zł (kwota stała)</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <MuiTextField
                    type="number"
                    size="small"
                    value={rule.value}
                    onChange={e => setRules(r => ({ ...r, [String(c)]: { ...r[String(c)], value: e.target.value } }))}
                    placeholder="np. 10"
                    sx={{ width: 110 }}
                    inputProps={{ step: 1 }}
                    helperText="ujemna = taniej"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{calcExample(rule)}</Typography>
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
            <TableCell>
              <Box>
                <Typography variant="body2">Dziecko</Typography>
                <Typography variant="caption" color="text.secondary">zamiast dorosłego</Typography>
              </Box>
            </TableCell>
            <TableCell>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={rules.child?.type ?? 'fixed'}
                  onChange={e => setRules(r => ({ ...r, child: { ...r.child, type: e.target.value } }))}
                >
                  <MenuItem value="percent">% (procent)</MenuItem>
                  <MenuItem value="fixed">zł (kwota stała)</MenuItem>
                </Select>
              </FormControl>
            </TableCell>
            <TableCell>
              <MuiTextField
                type="number"
                size="small"
                value={rules.child?.value ?? ''}
                onChange={e => setRules(r => ({ ...r, child: { ...r.child, value: e.target.value } }))}
                placeholder="np. -50"
                sx={{ width: 110 }}
                inputProps={{ step: 1 }}
                helperText="np. -50 = tańsze"
              />
            </TableCell>
            <TableCell>
              <Typography variant="caption" color="text.secondary">
                {calcExample(rules.child ?? { type: 'fixed', value: '' })}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Box mt={1.5}>
        <Button onClick={handleSave} disabled={isPending} variant="contained" size="small">
          {isPending ? <CircularProgress size={16} /> : 'Zapisz reguły'}
        </Button>
      </Box>
    </Box>
  );
};

/** Sekcja szablonów sezonów — do użycia w zakładce konfiguracji. */
const SeasonConfigSection = () => {
  const record = useRecordContext();
  const { data: seasons = [] } = useGetList('season-templates', {
    filter: { apartment: record?.id },
    sort: { field: 'start_month', order: 'ASC' },
    pagination: { page: 1, perPage: 100 },
    enabled: Boolean(record?.id),
  });

  if (!record?.id) return null;
  const guestCounts = resolveGuestCounts(record);
  return <SeasonScheduleManager seasons={seasons} apartmentId={record.id} guestCounts={guestCounts} />;
};

const ApartmentPricingManager = () => {
  const record = useRecordContext();
  const navigate = useNavigate();
  const createPath = useCreatePath();

  if (!record || !record.id) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Save the apartment before adding pricing periods.
      </Alert>
    );
  }

  const handleCreatePricing = () => {
    const path = createPath({
      resource: 'apartment-prices',
      type: 'create',
      state: { record: { apartment: record.id } },
    });
    navigate(path);
  };

  return (
    <Box mt={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Okresy cenowe</Typography>
        <Button
          variant="contained"
          onClick={handleCreatePricing}
          startIcon={<AddIcon />}
        >
          Dodaj cennik
        </Button>
      </Box>
      <YearlyPricingView allowDelete />
    </Box>
  );
};

export const ApartmentEdit = props => (
  <Edit {...props}>
    <TabbedForm>
      <FormTab label="Details">
        <TextInput disabled source="id" />
        <TextInput source="name" />
        <TextInput source="location" />
        <TextInput source="description" />
        <TextInput source="booking_com_ical_url" />
        <TextInput source="amenities" />
        <TextInput source="custom_domain" label="Custom Domain" helperText="Enter your custom domain (e.g. 'myapartment.com')" />
        <BooleanInput source="domain_enabled" label="Enable Custom Domain" />
      </FormTab>
      <FormTab label="Descriptions">
        <TextInput 
          source="description_pl" 
          multiline 
          rows={6} 
          fullWidth 
          helperText="Description in Polish language (use HTML tags for formatting)"
        />
        <TextInput 
          source="description_en" 
          multiline 
          rows={6} 
          fullWidth 
          helperText="Description in English language (use HTML tags for formatting)"
        />
        <TextInput 
          source="description_de" 
          multiline 
          rows={6} 
          fullWidth 
          helperText="Description in German language (use HTML tags for formatting)"
        />
      </FormTab>
      <FormTab label="Pricing">
        <ApartmentPricingManager />
      </FormTab>
      <FormTab label="Price Configuration">
        <Typography variant="subtitle1" gutterBottom>
          Obsługiwane liczby gości
        </Typography>
        <SelectArrayInput
          source="supported_guest_counts"
          label={false}
          choices={GUEST_COUNT_CHOICES}
          parse={parseGuestCounts}
          format={formatGuestCounts}
          fullWidth
          helperText="Wybierz wszystkie warianty obłożenia. Wpisy cenowe będą wymagały wartości dla każdego z nich."
        />
        <PricingRulesEditor />
        <Divider sx={{ my: 2 }} />
        <SeasonConfigSection />
      </FormTab>
    </TabbedForm>
  </Edit>
);

export const ApartmentCreate = props => (
  <Create {...props}>
    <TabbedForm defaultValues={{ supported_guest_counts: DEFAULT_GUEST_COUNTS }}>
      <FormTab label="Details">
        <TextInput source="name" />
        <TextInput source="location" />
        <TextInput source="description" />
        <TextInput source="booking_com_ical_url" />
        <TextInput source="amenities" />
        <TextInput source="custom_domain" label="Custom Domain" helperText="Enter your custom domain (e.g. 'myapartment.com')" />
        <BooleanInput source="domain_enabled" label="Enable Custom Domain" />
      </FormTab>
      <FormTab label="Photos">
        <ImageInput source="photos" label="Photos" multiple accept="image/*"
                   format={formatPhotos} parse={parsePhotos}>
          <ImageField source="src" title="" />
        </ImageInput>
      </FormTab>
      <FormTab label="Capacity">
        <Typography variant="subtitle1" gutterBottom>
          Supported guest counts
        </Typography>
        <SelectArrayInput
          source="supported_guest_counts"
          label={false}
          choices={GUEST_COUNT_CHOICES}
          parse={parseGuestCounts}
          format={formatGuestCounts}
          fullWidth
          helperText="Select all occupancy sizes you can host. Pricing entries can be configured after saving."
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          Add pricing rules once the apartment is saved.
        </Alert>
      </FormTab>
    </TabbedForm>
  </Create>
);

export const ApartmentShow = props => (
  <Show {...props}>
    <TabbedShowLayout>
      <Tab label="Details">
        <TextField source="id" />
        <TextField source="name" />
        <TextField source="location" />
        <TextField source="description" />
        <TextField source="booking_com_ical_url" label="Booking.com iCal URL" />
        <TextField source="amenities" />
        <TextField source="custom_domain" label="Custom Domain" />
        <TextField source="domain_enabled" label="Domain Enabled" />
        <EditButton />
      </Tab>
      <Tab label="Photos">
        <PhotosManagement />
      </Tab>
      <Tab label="Descriptions">
        <DescriptionManagement />
      </Tab>
      <Tab label="Pricing">
        <YearlyPricingView />
      </Tab>
      <Tab label="Price Configuration">
        <Box mb={2}>
          <Typography variant="subtitle1" gutterBottom>
            Obsługiwane liczby gości
          </Typography>
          <SupportedGuestCountsView />
        </Box>
        <PricingRulesEditor />
        <Divider sx={{ my: 2 }} />
        <SeasonConfigSection />
      </Tab>
      <Tab label="Bookings">
        <ReferenceManyField reference="bookings" target="apartment_id" label="" sort={{ field: 'check_in', order: 'DESC' }}>
          <Datagrid rowClick="edit">
            <TextField source="id" />
            <TextField source="status" />
            <TextField source="source" />
            <DateField source="check_in" />
            <DateField source="check_out" />
            <NumberField source="num_guests" label="Guests" />
            <NumberField source="total" options={{ style: 'currency', currency: 'PLN' }} />
            <NightsField label="Nights" />
            <ReferenceField source="guest" reference="guests" link="show">
              <TextField source="name" />
            </ReferenceField>
            <AtPropertyOrArrivalField label="At Property / Arrival" />
            <ShowButton />
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </Tab>
      <Tab label="Comments">
        <CommentsManagement />
      </Tab>
    </TabbedShowLayout>
  </Show>
);
